// (Imports remain same)
import {
    Person as BorrowerIcon,
    Dashboard as DashboardIcon,
    Description as DescriptionIcon,
    ExpandLess,
    ExpandMore,
    Business as LenderIcon,
    SupervisorAccount as ManagerIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import LocationTracker from '../../utils/LocationTracker';
import Header from './Header';
import { userPermissions } from '../../Store/userSlice';
import { assetPath } from '../../utils/constants';

const drawerWidth = 240;

const DashboardLayout = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const userPermission = useSelector(userPermissions);

    const moduleList = userPermission.map((e) => e?.module);

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', permissionKey: 'dashboard' },
        {
            text: 'Users',
            icon: <PeopleIcon />,
            hasSubMenu: true,
            subMenuItems: [
                { text: 'Managers', icon: <ManagerIcon />, path: '/users/managers', permissionKey: 'managers' },
                { text: 'Consultants', icon: <ManagerIcon />, path: '/users/consultants', permissionKey: 'managers' },
                { text: 'Borrowers', icon: <BorrowerIcon />, path: '/users/borrowers', permissionKey: 'borrowers' },
                { text: 'Lenders', icon: <LenderIcon />, path: '/users/lenders', permissionKey: 'lenders' }
            ]
        },
        { text: 'Leads', icon: <PeopleIcon />, path: '/leads', permissionKey: 'leads' },
        { text: 'Proposals', icon: <DescriptionIcon />, path: '/proposals', permissionKey: 'loans' },
        // { text: 'Proposals', icon: <DescriptionIcon />, path: '/proposals', permissionKey: 'proposals' },
        { text: 'Offers', icon: <DescriptionIcon />, path: '/offers', permissionKey: 'offers' },
        { text: 'Content', icon: <DescriptionIcon />, path: '/blogs', permissionKey: 'blogs' }
    ];

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleUsersToggle = () => {
        setUsersOpen(!usersOpen);
    };

    const handleNavigation = (path) => {
        if (location.pathname === '/apply-loan' && path !== '/apply-loan') {
            const event = new CustomEvent('beforeNavigate', { detail: { target: path } });
            window.dispatchEvent(event);
        } else {
            navigate(path);
            if (isMobile) setMobileOpen(false);
        }
    };

    useEffect(() => {
        if (location?.pathname?.includes('/users')) {
            setUsersOpen(true);
        }
    }, [location.pathname]);

    const drawer = (
        <div style={{ backgroundColor: '#212121', height: '100%', color: 'white' }}>
            <Toolbar sx={{ backgroundColor: '#212121' }}>
                <Box
                    component="img"
                    sx={{ height: 40, width: 'auto', margin: 'auto' }}
                    alt="DeQuiTech Logo"
                    src={assetPath('Logo1.png')}
                />
            </Toolbar>
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
            <List>
                {menuItems.map((item) => {
                    // If submenu exists, show only if any subPermission exists
                    if (item.hasSubMenu && item.subMenuItems) {
                        const visibleSubItems = item.subMenuItems.filter(sub =>
                            moduleList.includes(sub.permissionKey)
                        );
                        if (visibleSubItems.length === 0) return null;

                        return (
                            <React.Fragment key={item.text}>
                                <ListItemButton onClick={handleUsersToggle} sx={{ cursor: 'pointer' }}>
                                    <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                    {usersOpen ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                                </ListItemButton>
                                <Collapse in={usersOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {visibleSubItems.map((subItem) => (
                                            <ListItemButton
                                                key={subItem.text}
                                                onClick={() => handleNavigation(subItem.path)}
                                                sx={{ pl: 4, cursor: 'pointer' }}
                                                selected={location.pathname === subItem.path}
                                            >
                                                <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>{subItem.icon}</ListItemIcon>
                                                <ListItemText primary={subItem.text} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    }

                    // No submenu → check direct permission
                    if (!moduleList.includes(item.permissionKey)) return null;

                    return (
                        <ListItemButton
                            key={item.text}
                            onClick={() => handleNavigation(item.path)}
                            sx={{ cursor: 'pointer' }}
                            selected={location.pathname === item.path}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    );
                })}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <LocationTracker />
            <Header drawerWidth={drawerWidth} handleDrawerToggle={handleDrawerToggle} />
            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            backgroundColor: '#212121',
                            borderRadius: 0,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            backgroundColor: '#212121',
                            borderRadius: 0,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    mt: '64px'
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default DashboardLayout;
